import * as request from "@/utils/request";
import { DefaultConfigs, inApp } from "@/common/js/constant";
import { PayeeParam, detailsInfo } from "./types";
import BusinessVerify, { EnumBusinessFlow, SDK_VERSION } from "@/components/BusinessVerify";
import RiskVerify, { RISK_SDK_VERSION } from "@/components/RiskVerify";
import { setCommonAttrs, trackEvent } from "@/utils/omega";
import APP from "@/utils/app";
import { ERROR_MESSAGE } from "@/utils/request";
import { getBioFlag } from "@/utils/utils";

const baseUrl = `${DefaultConfigs.publicAPIPath}`;
// 拼接页面url上的参数
const queryParams = new URLSearchParams(window.location.search);
// const setUrl = (url:string, tradeId:string)=>{
//   url = `${url}?${queryParams.toString()}&transactionId=${tradeId}`
//   return url
// };

type detailsParams = {
  transactionId: string;
};

// 查询账单流水详情
// 账单详情页面新增保存收款人按钮
// /v0/bill/detail接口新增下游：/user/payee/getDetail，判断一个账户是否已经被保存为收款人
// /v0/bill/detail接口返回值新增字段：needSavePayee，表示是否要展示保存收款人按钮
// /v0/bill/detail接口返回值新增字段：transactionId，作为后面请求保存收款人接口的入参
// 接口文档：http://xmock.intra.xiaojukeji.com/project/420/interface/api/17764
// 请求保存收款人接口需要的参数从当前页面取（只需要transactionId即可）
export function getDetailInfo(params: detailsParams) {
  return request.get<detailsInfo>(
    `${baseUrl}/sofipo/v0/bill/detail?transactionId=${params.transactionId}`,
    {},
  );
}

// 保存收款人
// 新增保存收款人接口
// 接口文档：http://xmock.intra.xiaojukeji.com/project/420/interface/api/27595
// 该接口有两套参数，分别给 收款人信息页面 和 账单详情页面 使用
// 该接口需要做业务核验
export async function savePayee(params: PayeeParam): Promise<void> {
  // 核验版本
  params.verifyInfo = params.verifyInfo || {
    verifySdkVersion: SDK_VERSION,
  };

  try {
    // 提交
    return await request.post<void>(`${baseUrl}/sofipo/v0/trade/payee/create`, params);
  } catch (ret: any) {
    // 业务核验
    if (ret.errno === request.ERROR_TYPE.BusinessVerify) {
      // 设置公参
      setCommonAttrs({
        // scene_source
        // 场景来源
        // 0-风控
        // 1-业务
        scene_source: BusinessVerify.scene,
        // from_source 流程来源
        from_source: EnumBusinessFlow.payeeAfterOrder,
      });

      // 核验拉起时禁止返回按钮
      if (inApp) {
        APP.requestBackPressedControl(() => {});
      }
      // 拉起核验
      return new BusinessVerify()
        .verify(JSON.parse(ret?.data?.bussVerifyResultInfo)?.atoDomain)
        .then((res) => {
          // 重置公参
          setCommonAttrs({
            // scene_source
            // 场景来源
            // 0-风控
            // 1-业务
            scene_source: undefined,
            // from_source 流程来源
            from_source: undefined,
          });
          // 释放控制
          if (inApp) {
            APP.cancelBackPressedControl();
          }
          // 核验完成后删掉order id参数
          setCommonAttrs({
            order_id: undefined,
          });

          // 增加核验token
          if (params.verifyInfo) {
            params.verifyInfo.sessionId = res.verify_session_id;
          }
          // 重启发起请求
          return savePayee(params);
        })
        .catch((err) => {
          // 释放控制
          if (inApp) {
            APP.cancelBackPressedControl();
          }
          // 技术打点
          trackEvent("tech_sofi_catch_error", {
            source: "billDetail.savePayee.BusinessVerify",
            data: JSON.stringify(err),
          });
          // 透出给交互层
          return Promise.reject(err);
        });
    }
    return Promise.reject(ret);
  }
}

// 核验结果枚举
export enum VerifyResult {
  Success = 1,
  Fail = 2,
  Cancel = 3,
}

// 核验场景枚举
export enum VerifyScene {
  Cancel = 1,
  Release = 2,
}

// 核验错误码
export const VERIFY_ERROR_NO = {
  VerifyLimitReached: 102700006,
} as const;

// 核验结果埋点
const trackVerifyResult = (
  tradeId: string,
  verificationResult: VerifyResult,
  verifyScene: VerifyScene,
) => {
  trackEvent("fin_sofi_home_balance_statement_detail_bt", {
    transaction_id: tradeId,
    verification_result: String(verificationResult),
    verify_scene: String(verifyScene),
  });
};

type RiskHoldParams = {
  tradeId: string;
  verifyInfo?: {
    bussVerify?: string;
    sessionId?: string;
    verifySdkVersion?: string;
    bioFlag?: number;
  };
  riskInfo?: {
    token: string;
    sessionId: string;
    verifySdkVersion: string;
  };
};

type RiskHoldResponse = {
  tradeId: string;
  uid: string;
  status: number;
  needBussVerify?: number;
  bussVerifyResultInfo?: any;
  riskInfo?: string;
};

// 取消延迟出金交易
export async function cancelRiskHold(params: RiskHoldParams): Promise<RiskHoldResponse> {
  params.verifyInfo = params.verifyInfo || {
    verifySdkVersion: RISK_SDK_VERSION,
  };

  try {
    const ret = await request.post<RiskHoldResponse>(
      `${baseUrl}/sofipo/v0/trade/riskHold/cancel`,
      params,
    );
    // needBussVerify=1 需要业务核验
    if (ret.needBussVerify === 1 && ret.bussVerifyResultInfo) {
      const bussVerifyData =
        typeof ret.bussVerifyResultInfo === "string"
          ? JSON.parse(ret.bussVerifyResultInfo)
          : ret.bussVerifyResultInfo;

      setCommonAttrs({
        order_id: ret.tradeId,
        scene_source: BusinessVerify.scene,
        from_source: EnumBusinessFlow.riskHoldCancel,
      });

      if (inApp) {
        APP.requestBackPressedControl(() => {});
      }

      return new BusinessVerify()
        .verify(bussVerifyData?.atoDomain)
        .then((res) => {
          setCommonAttrs({ order_id: undefined, scene_source: undefined, from_source: undefined });
          if (inApp) {
            APP.cancelBackPressedControl();
          }

          if (params.verifyInfo) {
            params.verifyInfo.sessionId = res.verify_session_id;
            if (res.verify_session_id) {
              params.verifyInfo.bussVerify = "1";
            }
          }
          trackVerifyResult(ret.tradeId, VerifyResult.Success, VerifyScene.Cancel);
          return cancelRiskHold(params);
        })
        .catch((err) => {
          setCommonAttrs({ order_id: undefined, scene_source: undefined, from_source: undefined });
          if (inApp) {
            APP.cancelBackPressedControl();
          }
          trackVerifyResult(ret.tradeId, VerifyResult.Cancel, VerifyScene.Cancel);
          trackEvent("tech_sofi_catch_error", {
            source: "billDetail.cancelRiskHold.BusinessVerify",
            data: JSON.stringify(err),
          });
          return Promise.reject({ message: ERROR_MESSAGE.RiskVerifyFailed });
        });
    }

    return ret;
  } catch (ret: any) {
    if (ret.errno === request.ERROR_TYPE.RiskVerify) {
      setCommonAttrs({ order_id: ret?.data?.tradeId });
      params.verifyInfo = { verifySdkVersion: RISK_SDK_VERSION };

      if (inApp) {
        APP.requestBackPressedControl(() => {});
      }

      return new RiskVerify()
        .verify(JSON.parse(ret?.data?.bussVerifyResultInfo)?.atoDomain)
        .then(async (res) => {
          setCommonAttrs({ order_id: undefined });
          if (inApp) {
            APP.cancelBackPressedControl();
          }

          params.riskInfo = {
            token: res.verify_data || "",
            sessionId: res.verify_session_id || "",
            verifySdkVersion: res.verifySdkVersion || "",
          };
          params.verifyInfo = {
            verifySdkVersion: RISK_SDK_VERSION,
            bioFlag: await getBioFlag(),
          };
          trackVerifyResult(ret?.data?.tradeId, VerifyResult.Success, VerifyScene.Cancel);
          return cancelRiskHold(params);
        })
        .catch((err) => {
          if (inApp) {
            APP.cancelBackPressedControl();
          }
          trackVerifyResult(ret?.data?.tradeId, VerifyResult.Cancel, VerifyScene.Cancel);
          trackEvent("tech_sofi_catch_error", {
            source: "billDetail.cancelRiskHold.RiskVerify",
            data: JSON.stringify(err),
          });
          return Promise.reject({ message: ERROR_MESSAGE.RiskVerifyFailed });
        });
    }

    if (ret.errno === request.ERROR_TYPE.BusinessVerify) {
      setCommonAttrs({
        order_id: ret?.data?.tradeId,
        scene_source: BusinessVerify.scene,
        from_source: EnumBusinessFlow.riskHoldCancel,
      });

      if (inApp) {
        APP.requestBackPressedControl(() => {});
      }

      const bussVerifyData =
        typeof ret?.data?.bussVerifyResultInfo === "string"
          ? JSON.parse(ret.data.bussVerifyResultInfo)
          : ret?.data?.bussVerifyResultInfo;

      return new BusinessVerify()
        .verify(bussVerifyData?.atoDomain)
        .then((res) => {
          setCommonAttrs({ order_id: undefined, scene_source: undefined, from_source: undefined });
          if (inApp) {
            APP.cancelBackPressedControl();
          }

          if (params.verifyInfo) {
            params.verifyInfo.sessionId = res.verify_session_id;
          }
          trackVerifyResult(ret?.data?.tradeId, VerifyResult.Success, VerifyScene.Cancel);
          return cancelRiskHold(params);
        })
        .catch((err) => {
          setCommonAttrs({ order_id: undefined, scene_source: undefined, from_source: undefined });
          if (inApp) {
            APP.cancelBackPressedControl();
          }
          trackVerifyResult(ret?.data?.tradeId, VerifyResult.Cancel, VerifyScene.Cancel);
          trackEvent("tech_sofi_catch_error", {
            source: "billDetail.cancelRiskHold.BusinessVerify",
            data: JSON.stringify(err),
          });
          return Promise.reject({ message: ERROR_MESSAGE.RiskVerifyFailed });
        });
    }

    return Promise.reject(ret);
  }
}

// 确认继续转出
export async function releaseRiskHold(params: RiskHoldParams): Promise<RiskHoldResponse> {
  params.verifyInfo = params.verifyInfo || {
    verifySdkVersion: RISK_SDK_VERSION,
  };

  try {
    const ret = await request.post<RiskHoldResponse>(
      `${baseUrl}/sofipo/v0/trade/riskHold/release`,
      params,
    );

    // needBussVerify=1 需要业务核验
    if (ret.needBussVerify === 1 && ret.bussVerifyResultInfo) {
      const bussVerifyData =
        typeof ret.bussVerifyResultInfo === "string"
          ? JSON.parse(ret.bussVerifyResultInfo)
          : ret.bussVerifyResultInfo;

      setCommonAttrs({
        order_id: ret.tradeId,
        scene_source: BusinessVerify.scene,
        from_source: EnumBusinessFlow.riskHoldRelease,
      });

      if (inApp) {
        APP.requestBackPressedControl(() => {});
      }

      return new BusinessVerify()
        .verify(bussVerifyData?.atoDomain)
        .then((res) => {
          setCommonAttrs({ order_id: undefined, scene_source: undefined, from_source: undefined });
          if (inApp) {
            APP.cancelBackPressedControl();
          }

          if (params.verifyInfo) {
            params.verifyInfo.sessionId = res.verify_session_id;
            if (res.verify_session_id) {
              params.verifyInfo.bussVerify = "1";
            }
          }
          trackVerifyResult(ret.tradeId, VerifyResult.Success, VerifyScene.Release);
          return releaseRiskHold(params);
        })
        .catch((err) => {
          setCommonAttrs({ order_id: undefined, scene_source: undefined, from_source: undefined });
          if (inApp) {
            APP.cancelBackPressedControl();
          }
          trackVerifyResult(ret.tradeId, VerifyResult.Cancel, VerifyScene.Release);
          trackEvent("tech_sofi_catch_error", {
            source: "billDetail.releaseRiskHold.BusinessVerify",
            data: JSON.stringify(err),
          });
          return Promise.reject({ message: ERROR_MESSAGE.RiskVerifyFailed });
        });
    }

    return ret;
  } catch (ret: any) {
    if (ret.errno === request.ERROR_TYPE.RiskVerify) {
      setCommonAttrs({ order_id: ret?.data?.tradeId });
      params.verifyInfo = { verifySdkVersion: RISK_SDK_VERSION };

      if (inApp) {
        APP.requestBackPressedControl(() => {});
      }

      return new RiskVerify()
        .verify(JSON.parse(ret?.data?.bussVerifyResultInfo)?.atoDomain)
        .then(async (res) => {
          setCommonAttrs({ order_id: undefined });
          if (inApp) {
            APP.cancelBackPressedControl();
          }

          params.riskInfo = {
            token: res.verify_data || "",
            sessionId: res.verify_session_id || "",
            verifySdkVersion: res.verifySdkVersion || "",
          };
          params.verifyInfo = {
            verifySdkVersion: RISK_SDK_VERSION,
            bioFlag: await getBioFlag(),
          };
          trackVerifyResult(ret?.data?.tradeId, VerifyResult.Success, VerifyScene.Release);
          return releaseRiskHold(params);
        })
        .catch((err) => {
          if (inApp) {
            APP.cancelBackPressedControl();
          }
          trackVerifyResult(ret?.data?.tradeId, VerifyResult.Cancel, VerifyScene.Release);
          trackEvent("tech_sofi_catch_error", {
            source: "billDetail.releaseRiskHold.RiskVerify",
            data: JSON.stringify(err),
          });
          return Promise.reject({ message: ERROR_MESSAGE.RiskVerifyFailed });
        });
    }

    if (ret.errno === request.ERROR_TYPE.BusinessVerify) {
      setCommonAttrs({
        order_id: ret?.data?.tradeId,
        scene_source: BusinessVerify.scene,
        from_source: EnumBusinessFlow.riskHoldRelease,
      });

      if (inApp) {
        APP.requestBackPressedControl(() => {});
      }

      const bussVerifyData =
        typeof ret?.data?.bussVerifyResultInfo === "string"
          ? JSON.parse(ret.data.bussVerifyResultInfo)
          : ret?.data?.bussVerifyResultInfo;

      return new BusinessVerify()
        .verify(bussVerifyData?.atoDomain)
        .then((res) => {
          setCommonAttrs({ order_id: undefined, scene_source: undefined, from_source: undefined });
          if (inApp) {
            APP.cancelBackPressedControl();
          }

          if (params.verifyInfo) {
            params.verifyInfo.sessionId = res.verify_session_id;
          }
          trackVerifyResult(ret?.data?.tradeId, VerifyResult.Success, VerifyScene.Release);
          return releaseRiskHold(params);
        })
        .catch((err) => {
          setCommonAttrs({ order_id: undefined, scene_source: undefined, from_source: undefined });
          if (inApp) {
            APP.cancelBackPressedControl();
          }
          trackVerifyResult(ret?.data?.tradeId, VerifyResult.Cancel, VerifyScene.Release);
          trackEvent("tech_sofi_catch_error", {
            source: "billDetail.releaseRiskHold.BusinessVerify",
            data: JSON.stringify(err),
          });
          return Promise.reject({ message: ERROR_MESSAGE.RiskVerifyFailed });
        });
    }

    return Promise.reject(ret);
  }
}
